<script lang="ts">
    import { onMount } from 'svelte';
    import { throttle } from 'throttle-debounce';
    import { initSounds } from '$lib/services/audio/service';
    import { sendCallEvent, sendStoriesEvent } from '$lib/services/gtm/methods';

    import type { StoryFeedItem } from '@api/story/types';
    import { OverlayWrapper } from '@components/common/overlay-wrapper';
    import { Slider } from '@components/common/slider';
    import type { ScrollDirection, SliderRef } from '@components/common/slider/types';
    import { Logo } from '@components/layout/header/components/logo';
    import { paymentStore, totalMinutes } from '@components/payment/store';
    import { searchStore } from '@components/room/search';
    import { globalStore } from '@components/stores';
    import { dialogStore } from '@components/ui/dialog';
    import { isOperator } from '@components/user/store';
    import { LL } from '@i18n/i18n-svelte';
    import { USER_MINUTES_BOUNDARY } from '@shared/constants';
    import { URLS } from '@shared/routing';
    import {
        CallContext,
        EventType,
        Orientation,
        PaymentSource,
        SignupContext,
        UserType,
    } from '@shared/types';

    import { FeedControls } from './components/controls';
    import { FeedInfo } from './components/info';
    import { StoryPlayer } from './components/player';
    import { SwipeInfo } from './components/swipe-info';
    import { FEED_BLOCKING_TIME, ITEM_LEFT_THRESHOLD, LIMIT_PER_PAGE } from './constants';
    import {
        isStoryFeedBlocked,
        lastWatchedStoryIndex,
        limitedStories,
        storyFeedStore,
    } from './store';

    import s from './feed.module.css';

    import { goto } from '$app/navigation';
    import { page as pageStore } from '$app/stores';

    export let userId: string | null = null;

    let sliderRef: SliderRef;
    let isCurrentStoryWatched = false;
    let currentStoryIndex = $lastWatchedStoryIndex;
    let lastStoryIndex: number;
    $: lastStoryIndex = $storyFeedStore.stories.length - 1;
    let lastLimitedStoryIndex: number;
    $: lastLimitedStoryIndex = $limitedStories.length - 1;

    let currentStory: StoryFeedItem | undefined = undefined;
    $: currentStory = $limitedStories[currentStoryIndex];
    let currentPage: number;
    $: currentPage = Math.ceil($storyFeedStore.stories.length / LIMIT_PER_PAGE);
    let lastPage: number;
    $: lastPage = Math.ceil($storyFeedStore.storiesCount / LIMIT_PER_PAGE);

    function closeDialog() {
        if (isStoryFeedBlocked) {
            goto(URLS.storyClose());
        }

        dialogStore.closeDialog();
    }

    function showDialog() {
        const countdownEndDate = $storyFeedStore.blockedDate
            ? new Date($storyFeedStore.blockedDate + FEED_BLOCKING_TIME).getTime()
            : undefined;
        if (userId) {
            dialogStore.showBuyMembership(
                {
                    source: PaymentSource.Stories,
                    countdownEndDate,
                    onCountdownFinished: () => {
                        dialogStore.closeDialog();
                        storyFeedStore.setBlocking(false);
                    },
                },
                closeDialog,
                true
            );
        } else {
            dialogStore.showSignup(
                {
                    place: SignupContext.StoriesLimit,
                    isMinified: true,
                    countdownEndDate,
                    onCountdownFinished: () => {
                        dialogStore.closeDialog();
                        storyFeedStore.setBlocking(false);
                    },
                    title: $LL.auth.story.title(),
                },
                closeDialog,
                true
            );
        }
    }

    let isOpen = false;

    onMount(() => pageStore.subscribe(async ({ url }) => {
        const newIsOpen = url.href.startsWith(URLS.storyOpen());
        if (newIsOpen && isOpen) {
            return;
        }
        let storyId = newIsOpen ? url.href.replace(URLS.storyOpen(), '') : '';
        if (newIsOpen) {
            if (storyId) {
                storyFeedStore.setLastWatchedId(storyId);
            }
            await storyFeedStore.loadStories(Boolean(userId), storyId || undefined);
        }
        isOpen = newIsOpen;

        if (isOpen) {
            currentStoryIndex = $lastWatchedStoryIndex;

            sendStoriesEvent({
                event: EventType.StoriesStart,
                stories_count: currentStoryIndex + 1,
                direction: 'start',
            });

            if ($isStoryFeedBlocked) {
                showDialog();
            }
        }
    }));

    function onTimeUpdate(event: Event) {
        if (isCurrentStoryWatched || !event.currentTarget) {
            return;
        }

        const video = event.currentTarget as HTMLVideoElement;
        if (video.currentTime > (video.duration / 2)) {
            sendStoriesEvent({ event: EventType.StoriesViewSuccess });

            isCurrentStoryWatched = true;
            const watchedStory = $limitedStories?.[currentStoryIndex];
            if (watchedStory) {
                storyFeedStore.markStoryWatched(watchedStory.id);
            }
        }
    }

    const onSlideChange = (currentIndex: number, prevIndex: number, direction: ScrollDirection) => {
        storyFeedStore.setMute(true);
        isCurrentStoryWatched = false;
        currentStoryIndex = currentIndex;

        sendStoriesEvent({
            event: EventType.StoriesStart,
            stories_count: currentIndex + 1,
            direction: direction,
        });

        if (currentIndex > lastStoryIndex - ITEM_LEFT_THRESHOLD) {
            const nextPage = currentPage + 1;
            if (lastPage >= nextPage) {
                storyFeedStore.addStories(Boolean(userId), nextPage);
            }
        }

        if (currentIndex >= lastLimitedStoryIndex || currentIndex >= lastStoryIndex) {
            storyFeedStore.setBlocking(true);
            if ($isStoryFeedBlocked) {
                showDialog();
            }
        }

        storyFeedStore.setLastWatchedId($limitedStories?.[currentIndex]?.id ?? null);
    };

    function close(e?: Event) {
        e?.preventDefault();
        onClose();
        goto(URLS.storyClose());
    }

    function onClose() {
        sendStoriesEvent({
            event: EventType.StoriesClose,
            stories_count: currentStoryIndex + 1,
        });
    }

    const callHandler = (story: StoryFeedItem) => {
        sendCallEvent({
            event: EventType.CallButtonClick,
            type: UserType.User,
            place: CallContext.Stories,
        });

        if (
            !$isOperator &&
            (!$totalMinutes || $totalMinutes <= USER_MINUTES_BOUNDARY || $paymentStore.currentSubscription?.hasGift)
        ) {
            paymentStore.showBuyDialog(PaymentSource.Call);
            return;
        }

        initSounds();
        searchStore.setOutgoingCall(null, story.operatorDisplayName, story.operatorId);
        goto(URLS.roomCall({ source: CallContext.Stories }));
    };
</script>

<OverlayWrapper bind:isOpen>
    <div class={s.root}>
        <Logo classes={s.logo} on:click={close}/>

        {#if !$isStoryFeedBlocked && currentStory}
            <div class={s.mobileInfoContainer}>
                <FeedInfo story={currentStory} isDesktop={$globalStore.isDesktopSite} {userId} {callHandler} />
            </div>
        {/if}

        <div class={s.sliderContainer}>
            <Slider
                bind:this={sliderRef}
                data={$limitedStories}
                let:dataItem
                currentIndex={currentStoryIndex}
                {onSlideChange}
            >
                <StoryPlayer
                    isCovered={
                        $globalStore.orientation === Orientation.Vertical
                        && dataItem.orientation === Orientation.Vertical
                        && !$globalStore.isDesktopSite
                    }
                    story={dataItem}
                    isBlocked={$isStoryFeedBlocked}
                    on:timeupdate={onTimeUpdate}
                />
            </Slider>
        </div>

        {#if !$isStoryFeedBlocked && currentStory}
            <div class={s.mobileButtonContainer}>
                <FeedControls
                    {userId}
                    nextSlide={throttle(600, () => sliderRef.nextSlide(), { noTrailing: true })}
                    prevSlide={throttle(600, () => sliderRef.prevSlide(), { noTrailing: true })}
                    {onClose}
                    story={currentStory}
                    disablePrev={currentStoryIndex === 0}
                />
            </div>
            <SwipeInfo />
        {/if}
    </div>
</OverlayWrapper>
